/**
 * FlowPage - Main flow visualization page
 *
 * This page displays the flow visualization with a sidebar for flow management.
 * Supports full-screen mode and collapsible sidebar on mobile.
 *
 * Phase 3 of WP-FLOW-5: Interactivity - tooltips, click handlers, navigation.
 * WP-TRANSFER-1: Added transfer processing indicator support.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useFlows, useFlow, useFlowSnapshot, useCreateFlow, useDeleteFlow, useRegenerateFlow } from '@/hooks/useFlows';
import { useAccounts } from '@/hooks/useAccounts';
import {
  FlowSidebar,
  FlowHeader,
  FlowEmpty,
  CreateFlowModal,
  FlowExportModal,
  TransactionDrawer,
} from '@/components/flows';
import {
  FlowEmptyState,
  FlowVisualization,
  useFlowData,
  type FlowEmptyStateReason,
} from '@/components/flow';
import Card from '@/components/ui/Card';
import Spinner from '@/components/ui/Spinner';
import Alert from '@/components/ui/Alert';
import { cn } from '@/utils/cn';
import { getRateLimitRetryMessage } from '@/utils/errorMessages';
import type { CreateFlowRequest } from '@/services/flows';
import type { TransactionWithSplits } from '@/services/transactions';
import { useTransferProcessingState } from '@/hooks/useTransferProcessingState';

/**
 * State for the transaction drawer
 */
interface TransactionDrawerState {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  isLoading: boolean;
  transactions: TransactionWithSplits[];
  totalAmount?: number;
  transactionCount?: number;
  categoryId?: string;
}

export default function FlowPage() {
  const { flowId } = useParams<{ flowId?: string }>();
  const navigate = useNavigate();

  // State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [transactionDrawer, setTransactionDrawer] = useState<TransactionDrawerState>({
    isOpen: false,
    title: '',
    isLoading: false,
    transactions: [],
  });
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // Queries
  const { data: flows, isLoading: isLoadingFlows, error: flowsError } = useFlows();
  const { data: selectedFlow, isLoading: isLoadingFlow } = useFlow(flowId);
  const {
    data: snapshot,
    isLoading: isLoadingSnapshot,
    error: snapshotError,
    isRefetching: isRefetchingSnapshot,
    refetch: refetchSnapshot,
  } = useFlowSnapshot(flowId);
  const { data: accounts, isLoading: isLoadingAccounts } = useAccounts();

  // WP-TRANSFER-1: Track transfer processing state
  const { isProcessing: isTransfersProcessing } = useTransferProcessingState();

  // Mutations
  const createFlowMutation = useCreateFlow();
  const deleteFlowMutation = useDeleteFlow();
  const regenerateFlowMutation = useRegenerateFlow();

  // A refresh refused by the household's flow generation limit (429, WP-CORE-8):
  // the server's message says when the user can try again.
  const refreshRateLimitMessage = getRateLimitRetryMessage(regenerateFlowMutation.error);
  // Loading the flow refused by the household's calculation limit (429, WP-CORE-9).
  const snapshotRateLimitMessage = getRateLimitRetryMessage(snapshotError);

  // Transform snapshot data for visualization
  const flowData = useFlowData(snapshot);

  // Track expanded tiers for visualization
  const [expandedTiers, setExpandedTiers] = useState<string[]>([]);

  /**
   * Handle tier expansion in the visualization
   */
  const handleTierExpand = useCallback((tier: string) => {
    setExpandedTiers((prev) =>
      prev.includes(tier)
        ? prev.filter((t) => t !== tier)
        : [...prev, tier]
    );
  }, []);

  /**
   * Determine the empty state reason when snapshot has no nodes/edges
   * Priority: error > loading > no-accounts > no-transactions > no-data-in-range
   */
  const emptyStateReason = useMemo((): FlowEmptyStateReason | null => {
    // If there's a snapshot error, show error state
    if (snapshotError) {
      return 'error';
    }

    // If still loading data, show loading state
    if (isLoadingFlow || isLoadingSnapshot || isLoadingAccounts) {
      return 'loading';
    }

    // If we have a snapshot with nodes, no empty state needed
    if (snapshot?.nodes && snapshot.nodes.length > 0) {
      return null;
    }

    // Check if user has any accounts at all
    if (!accounts || accounts.length === 0) {
      return 'no-accounts';
    }

    // We have accounts but no data in the snapshot
    // Determine if it's a general "no transactions" or "no data in range" issue
    // If the snapshot exists but is empty, it's likely no data for this specific period
    if (snapshot) {
      return 'no-data-in-range';
    }

    // Default: no transactions at all
    return 'no-transactions';
  }, [
    snapshotError,
    isLoadingFlow,
    isLoadingSnapshot,
    isLoadingAccounts,
    snapshot,
    accounts,
  ]);

  /**
   * Format the period label for the empty state message
   */
  const periodLabel = useMemo(() => {
    if (!snapshot?.periodStart || !snapshot?.periodEnd) {
      return undefined;
    }
    const startDate = new Date(snapshot.periodStart);
    const endDate = new Date(snapshot.periodEnd);
    const formatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${formatter.format(startDate)} - ${formatter.format(endDate)}`;
  }, [snapshot?.periodStart, snapshot?.periodEnd]);

  // Auto-select first flow if none selected
  useEffect(() => {
    if (!flowId && flows && flows.length > 0 && flows[0]) {
      navigate(`/flow/${flows[0].id}`, { replace: true });
    }
  }, [flowId, flows, navigate]);

  // Handle responsive sidebar collapse
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1024px)');

    const handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsSidebarCollapsed(e.matches);
    };

    handleMediaChange(mediaQuery);
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  // Handlers
  const handleSelectFlow = useCallback(
    (id: string) => {
      navigate(`/flow/${id}`);
    },
    [navigate]
  );

  const handleCreateFlow = useCallback(async (data: CreateFlowRequest) => {
    try {
      const newFlow = await createFlowMutation.mutateAsync(data);
      setIsCreateModalOpen(false);
      navigate(`/flow/${newFlow.id}`);
    } catch {
      // Error is handled by mutation state and displayed in modal
    }
  }, [createFlowMutation, navigate]);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    // Reset mutation state so error doesn't persist when reopening
    createFlowMutation.reset();
  }, [createFlowMutation]);

  const handleDeleteFlow = useCallback(
    async (id: string) => {
      // Navigate away BEFORE the mutation to prevent refetch of deleted flow
      // This avoids 404 errors from useFlow trying to fetch the deleted flow
      if (id === flowId && flows) {
        const remainingFlows = flows.filter((f) => f.id !== id);
        if (remainingFlows.length > 0 && remainingFlows[0]) {
          navigate(`/flow/${remainingFlows[0].id}`, { replace: true });
        } else {
          navigate('/flow', { replace: true });
        }
      }

      try {
        await deleteFlowMutation.mutateAsync(id);
      } catch (error) {
        // If delete fails after navigation, the error toast will show via mutation state
        // The user has already navigated away, which is acceptable UX
        // (preferable to showing 404 errors in console)
        if (import.meta.env.DEV) {
          console.error('Failed to delete flow:', error);
        }
        // Send to Sentry in production (no PII - only operation info)
        Sentry.captureException(error, {
          extra: { operation: 'deleteFlow' },
        });
      }
    },
    [deleteFlowMutation, flowId, flows, navigate]
  );

  const handleToggleFullScreen = useCallback(() => {
    setIsFullScreen((prev) => !prev);
  }, []);

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => !prev);
  }, []);

  const handleRefresh = useCallback(() => {
    if (flowId) {
      regenerateFlowMutation.mutate(flowId);
    }
  }, [flowId, regenerateFlowMutation]);

  /**
   * "Try Again" on the error state. After a calculation-limit 429 it re-reads
   * the flow (refetch) instead of regenerating: a regenerate would spend the
   * generation budget and succeed at once, contradicting "try again in N
   * minutes". A refused re-read is not counted, shows the message again with
   * the updated wait, and loads the flow once the window has passed.
   */
  const handleRetryLoad = useCallback(() => {
    if (snapshotRateLimitMessage) {
      void refetchSnapshot();
    } else {
      handleRefresh();
    }
  }, [snapshotRateLimitMessage, refetchSnapshot, handleRefresh]);

  const handleExport = useCallback(() => {
    setIsExportModalOpen(true);
  }, []);

  const handleCloseExportModal = useCallback(() => {
    setIsExportModalOpen(false);
  }, []);

  /**
   * Navigate to connections page to connect an account
   */
  const handleConnectAccount = useCallback(() => {
    navigate('/connections');
  }, [navigate]);

  /**
   * Period change handler - currently a no-op as period selection is handled
   * via the flow header controls (FlowHeader component).
   */
  const handleChangePeriod = useCallback(() => {
    // Period selection is available via FlowHeader component controls
  }, []);

  /**
   * Close the transaction drawer
   */
  const handleCloseTransactionDrawer = useCallback(() => {
    setTransactionDrawer((prev) => ({ ...prev, isOpen: false }));
  }, []);

  /**
   * Navigate to transactions page filtered by category
   * WP-FLOW-4: Also filter by amount sign based on node type (income/expense)
   */
  const handleCategoryClick = useCallback(
    (categoryId: string, startDate: string, endDate: string, nodeType: 'income' | 'expense') => {
      const params = new URLSearchParams({
        categoryId: categoryId,
        startDate: startDate,
        endDate: endDate,
        // WP-FLOW-4: Filter by transaction sign - income = positive, expense = negative
        amountSign: nodeType === 'income' ? 'positive' : 'negative',
      });
      navigate(`/transactions?${params.toString()}`);
    },
    [navigate]
  );

  /**
   * Navigate to transactions page filtered by account
   */
  const handleAccountClick = useCallback(
    (accountId: string, startDate: string, endDate: string) => {
      const params = new URLSearchParams({
        accountId: accountId,
        startDate: startDate,
        endDate: endDate,
      });
      navigate(`/transactions?${params.toString()}`);
    },
    [navigate]
  );

  /**
   * Navigate to transactions page filtered by edge (source -> target flow)
   */
  const handleEdgeClick = useCallback(
    (sourceId: string, targetId: string, startDate: string, endDate: string) => {
      // For edge clicks, we filter by the relevant category or account
      // The sourceId/targetId follow patterns like "income-{categoryId}" or "account-{accountId}"
      const params = new URLSearchParams({
        startDate: startDate,
        endDate: endDate,
      });

      // Parse the source/target to determine filter type
      if (sourceId.startsWith('income-')) {
        params.set('categoryId', sourceId.replace('income-', ''));
      } else if (sourceId.startsWith('account-')) {
        params.set('accountId', sourceId.replace('account-', ''));
      }

      if (targetId.startsWith('expense-')) {
        params.set('categoryId', targetId.replace('expense-', ''));
      } else if (targetId.startsWith('account-')) {
        // For transfers, filter by the target account
        params.set('accountId', targetId.replace('account-', ''));
      }

      navigate(`/transactions?${params.toString()}`);
    },
    [navigate]
  );

  /**
   * Navigate to transactions page filtered for transfers between accounts
   * WP-TRANSFER-2: IDs may be null for external/untracked accounts
   */
  const handleTransferClick = useCallback(
    (sourceId: string | null, targetId: string | null, startDate: string, endDate: string) => {
      // For transfer clicks, we filter by transfer type and date range
      // Use whichever account is tracked (not null) as the primary filter
      const accountId = sourceId ?? targetId;
      if (!accountId) {
        // Both accounts are external - shouldn't happen but handle gracefully
        console.warn('Transfer click with no tracked accounts');
        return;
      }
      const params = new URLSearchParams({
        type: 'transfer',
        accountId: accountId,
        startDate: startDate,
        endDate: endDate,
      });
      navigate(`/transactions?${params.toString()}`);
    },
    [navigate]
  );

  // Render loading state
  if (isLoadingFlows) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  // Render error state
  if (flowsError) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <Alert variant="error" title="Failed to load flows">
          {flowsError instanceof Error ? flowsError.message : 'An unexpected error occurred'}
        </Alert>
      </div>
    );
  }

  // Render empty state (no flows)
  if (!flows || flows.length === 0) {
    // No flows AND no accounts: say so, rather than inviting the user to
    // create a flow they have no data for.
    //
    // The precedence documented on emptyStateReason above is
    // `error > loading > no-accounts > ...`, but this branch returned before
    // that could ever apply - so a brand-new user who had connected nothing was
    // shown "No flows yet / Create Your First Flow", and the button opened a
    // modal to build a flow out of transactions that do not exist. The honest
    // next step is "connect a bank account", which is what FlowEmptyState's
    // no-accounts reason offers, along with a button that starts it.
    //
    // Gated on isLoadingAccounts so the prompt is never shown while the
    // accounts request is still in flight.
    if (!isLoadingAccounts && (!accounts || accounts.length === 0)) {
      return (
        <Card className="h-full min-h-[600px]" padding="none">
          <FlowEmptyState
            reason="no-accounts"
            periodLabel={periodLabel}
            onConnectAccount={handleConnectAccount}
            onChangePeriod={handleChangePeriod}
            onRetry={handleRefresh}
            className="h-full"
          />
        </Card>
      );
    }

    return (
      <>
        <Card className="h-full min-h-[600px]" padding="none">
          <FlowEmpty onCreateFlow={() => setIsCreateModalOpen(true)} />
        </Card>

        <CreateFlowModal
          isOpen={isCreateModalOpen}
          onClose={handleCloseCreateModal}
          onSubmit={handleCreateFlow}
          isSubmitting={createFlowMutation.isPending}
          error={createFlowMutation.error}
        />
      </>
    );
  }

  // Main render with flows
  return (
    <div
      className={cn(
        'flex h-full',
        isFullScreen && 'fixed inset-0 z-40 bg-white'
      )}
    >
      {/* Sidebar */}
      {!isFullScreen && (
        <FlowSidebar
          flows={flows}
          selectedFlowId={flowId}
          onSelectFlow={handleSelectFlow}
          onCreateFlow={() => setIsCreateModalOpen(true)}
          onDeleteFlow={handleDeleteFlow}
          isLoading={isLoadingFlows}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        {selectedFlow && (
          <FlowHeader
            flowName={selectedFlow.name}
            snapshot={snapshot}
            isFullScreen={isFullScreen}
            onToggleFullScreen={handleToggleFullScreen}
            onRefresh={handleRefresh}
            isRefreshing={regenerateFlowMutation.isPending || isRefetchingSnapshot}
            onExport={flowId ? handleExport : undefined}
          />
        )}

        {/* Refresh refused by the flow generation limit (WP-CORE-8) */}
        {refreshRateLimitMessage && (
          <div className="px-4 pt-4 sm:px-6">
            <Alert
              variant="warning"
              title="Flow limit reached"
              onDismiss={() => regenerateFlowMutation.reset()}
            >
              {refreshRateLimitMessage}
            </Alert>
          </div>
        )}

        {/* Transfer processing indicator (WP-TRANSFER-1) */}
        {isTransfersProcessing && (
          <div className="flex items-center justify-center gap-2 text-sm text-amber-700 bg-amber-50 border-b border-amber-200 px-4 py-2">
            <Spinner size="sm" className="text-amber-600" />
            <span>Transfer detection in progress - flow data may update shortly</span>
          </div>
        )}

        {/* Flow visualization area */}
        <div className="flex-1 p-4 sm:p-6 overflow-auto" style={{ scrollbarGutter: 'stable' }}>
          {emptyStateReason ? (
            <FlowEmptyState
              reason={emptyStateReason}
              periodLabel={periodLabel}
              onConnectAccount={handleConnectAccount}
              onChangePeriod={handleChangePeriod}
              onRetry={handleRetryLoad}
              rateLimitMessage={snapshotRateLimitMessage}
              className="h-full"
            />
          ) : flowData ? (
            <FlowVisualization
              inflowNodes={flowData.inflowNodes}
              accountNodes={flowData.accountNodes}
              lumpedNodes={flowData.lumpedNodes}
              outflowNodes={flowData.outflowNodes}
              edges={flowData.edges}
              transfers={flowData.transfers}
              transferSankey={flowData.transferSankey}
              expandedTiers={expandedTiers}
              isLoading={isLoadingSnapshot || isRefetchingSnapshot}
              periodStart={snapshot?.periodStart}
              periodEnd={snapshot?.periodEnd}
              onTierExpand={handleTierExpand}
              onCategoryClick={handleCategoryClick}
              onAccountClick={handleAccountClick}
              onEdgeTransactionClick={handleEdgeClick}
              onTransferClick={handleTransferClick}
            />
          ) : null}
        </div>

        {/* Mobile sidebar toggle (when collapsed and not fullscreen) */}
        {isSidebarCollapsed && !isFullScreen && (
          <button
            type="button"
            onClick={handleToggleSidebar}
            className={cn(
              'fixed bottom-4 left-4 z-30 p-3 rounded-full shadow-lg',
              'bg-primary-600 text-white hover:bg-primary-700',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
              'lg:hidden'
            )}
            aria-label="Show flows sidebar"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Create flow modal */}
      <CreateFlowModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSubmit={handleCreateFlow}
        isSubmitting={createFlowMutation.isPending}
        error={createFlowMutation.error}
      />

      {/* Transaction drawer for node/edge click */}
      {snapshot && (
        <TransactionDrawer
          isOpen={transactionDrawer.isOpen}
          title={transactionDrawer.title}
          subtitle={transactionDrawer.subtitle}
          periodStart={snapshot.periodStart}
          periodEnd={snapshot.periodEnd}
          transactions={transactionDrawer.transactions}
          isLoading={transactionDrawer.isLoading}
          totalAmount={transactionDrawer.totalAmount}
          transactionCount={transactionDrawer.transactionCount}
          onClose={handleCloseTransactionDrawer}
        />
      )}

      {/* Export PDF modal */}
      {flowId && (
        <FlowExportModal
          isOpen={isExportModalOpen}
          onClose={handleCloseExportModal}
          flowId={flowId}
          flowName={selectedFlow?.name}
        />
      )}
    </div>
  );
}
